import { useState, useEffect, useCallback } from 'react'
import calendarService from '../services/calendarService'

/**
 * Custom hook for calendar functionality
 * Handles fetching historical events, birthdays, and holidays
 */
export const useCalendar = () => {
  const [events, setEvents] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedDate, setSelectedDate] = useState(new Date())

  // Load events for the currently selected date
  const loadEvents = useCallback(async (date = selectedDate) => {
    setLoading(true)
    setError(null)

    try {
      const month = date.getMonth() + 1
      const day = date.getDate()
      console.log(`📅 Loading calendar events for ${month}/${day}`)

      const data = await calendarService.getEventsForDate(month, day)
      const processedEvents = calendarService.processEventsForApp(data)
      
      setEvents(processedEvents)
      console.log(`📅 Loaded ${Object.keys(processedEvents).length} event categories`)
    } catch (err) {
      console.error('Error loading calendar events:', err)
      setError(err.message)
      // Set demo data as fallback
      setEvents(calendarService.processEventsForApp(calendarService.getDemoData(
        selectedDate.getMonth() + 1, 
        selectedDate.getDate()
      )))
    } finally {
      setLoading(false)
    }
  }, [selectedDate])

  // Load events when component mounts or date changes
  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  // Change the selected date
  const changeDate = useCallback((newDate) => {
    setSelectedDate(newDate)
  }, [])

  // Navigate to next day
  const nextDay = useCallback(() => {
    const nextDate = new Date(selectedDate)
    nextDate.setDate(nextDate.getDate() + 1)
    setSelectedDate(nextDate)
  }, [selectedDate])

  // Navigate to previous day  
  const previousDay = useCallback(() => {
    const prevDate = new Date(selectedDate)
    prevDate.setDate(prevDate.getDate() - 1)
    setSelectedDate(prevDate)
  }, [selectedDate])

  // Navigate to next month
  const nextMonth = useCallback(() => {
    const nextDate = new Date(selectedDate)
    nextDate.setMonth(nextDate.getMonth() + 1)
    setSelectedDate(nextDate)
  }, [selectedDate])

  // Navigate to previous month
  const previousMonth = useCallback(() => {
    const prevDate = new Date(selectedDate)
    prevDate.setMonth(prevDate.getMonth() - 1)
    setSelectedDate(prevDate)
  }, [selectedDate])

  // Navigate to today
  const goToToday = useCallback(() => {
    setSelectedDate(new Date())
  }, [])

  // Refresh events (bypass cache)
  const refresh = useCallback(() => {
    calendarService.clearCache()
    loadEvents()
  }, [loadEvents])

  return {
    // State
    events,
    loading,
    error,
    selectedDate,
    
    // Actions
    changeDate,
    nextDay,
    previousDay,
    nextMonth,
    previousMonth,
    goToToday,
    refresh,
    loadEvents
  }
}

/**
 * Hook specifically for calendar widget data
 */
export const useCalendarWidget = () => {
  const [widgetEvents, setWidgetEvents] = useState([])
  const [loading, setLoading] = useState(false)

  const loadWidgetEvents = useCallback(async () => {
    setLoading(true)
    try {
      const events = await calendarService.getWidgetEvents()
      setWidgetEvents(events)
    } catch (error) {
      console.error('Error loading calendar widget events:', error)
      setWidgetEvents(calendarService.getDemoWidgetData())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadWidgetEvents()
  }, [loadWidgetEvents])

  return {
    widgetEvents,
    loading,
    refresh: loadWidgetEvents
  }
}

export default useCalendar
